/**
Author - Brijesh Pandey
Git - https://bitbucket.org/__brijesh/
**/

import styled, {css} from "styled-components";

import {CountryDropdown} from "components/NewPartners/commons";
import React from "react";
import SafeLink from "components/commons/SafeLink";
import {Spinner} from "components";
import {WealthyEnv} from "helpers";

const loginUrl = `${WealthyEnv.URL}/partner#login`;
const downArrow = "https://i.wlycdn.com/wealthy-home-page/partners-dropdown-arrow.svg";
const infoIcon = "https://i.wlycdn.com/articles/info-icon-grey.svg";

const Form = ({
  resendOtp,
  isLoading,
  isPartnerExisting,
  submitForm,
  formData,
  handlePaste,
  setFormData,
  onChangeHandler,
  step,
  errorText,
  updateStep,
  onKeyUpHandler,
  counter,
  setCounter,
  isDisabled,
  isSubscribing,
  setIsSubscribing,
  isResendingOtp,
  setResendingOtp,
  onOtpChangeHandler,
  selectedCountryCode,
  showDropdown,
  setShowDropdown,
  handleCountryCodeChange,
  hasReferralCode,
  handleReferralCheckbox,
  handleReferralValidation,
  referralStatus
}) => {
  const numberOfOtpDigits = [...Array(5).keys()];

  const getReferralErrorMessage = () => {
    switch (referralStatus) {
      case "invalid":
        return formData.referralCode ? "Invalid referral code. Please try again." : "Please enter a referral code";
      case "valid":
        return "Referral code validated successfully";
      default:
        return "";
    }
  };

  const otpContent = numberOfOtpDigits.map(index => (
    <OtpInput
      autoFocus={index === 0 ? true : false}
      className="otp-input"
      data-index={index}
      id={index}
      key={index}
      maxLength="1"
      placeholder="__"
      type="tel"
      onChange={onOtpChangeHandler}
      onKeyUp={(event) => onKeyUpHandler(event.keyCode, index - 1, event.target, index + 1)}
      onPaste={handlePaste}
    />
  ));

  return (
    <>
      {
        step === 1 ? (
          <Wrapper>
            <TextContainer>
              <span className="step">Step 1:</span> Enter your Contact Details
            </TextContainer>

            <SubText className="join-us-subtext">
              Join a community of 14,000+ partners earning with Wealthy
            </SubText>

            <InputNameContainer>
              <Input
                className="input-first-name"
                name="firstName"
                placeholder="First Name"
                type="text"
                value={formData.firstName}
                onChange={onChangeHandler}
              />

              <Input
                className="input-last-name"
                name="lastName"
                placeholder="Last Name"
                type="text"
                value={formData.lastName}
                onChange={onChangeHandler}
              />
            </InputNameContainer>

            <InputNameContainer>
              <InputErrorContainer>
                <Input
                  className="input-email"
                  id="email"
                  name="email"
                  placeholder="Your email ID"
                  type="text"
                  value={formData.email}
                  onChange={onChangeHandler}
                />
                <FieldAdditionalInfo>Use AMFI registered Email if you have an ARN</FieldAdditionalInfo>
                <ErrorText>{errorText.email}</ErrorText>
              </InputErrorContainer>

              <InputErrorContainer>
                <PhoneNumberInput>
                  <DropDownWrapper onClick={() => setShowDropdown(!showDropdown)}>
                    <SelectedItem className="active">{selectedCountryCode.code}</SelectedItem>
                    <CountryDropdown
                      selectedItem={selectedCountryCode.code}
                      setShowDropdown={setShowDropdown}
                      showDropdown={showDropdown}
                      callback={handleCountryCodeChange}
                    />
                    <IconDd src={downArrow} alt="Dropdown arrow" key={showDropdown} $showDropdown={showDropdown} />
                  </DropDownWrapper>
                  <Input
                    className="input--phone-number"
                    id="phoneNumber"
                    name="phoneNumber"
                    placeholder="Enter phone number"
                    type="number"
                    value={formData.phoneNumber}
                    onChange={onChangeHandler}
                  />
                </PhoneNumberInput>
                <FieldAdditionalInfo>Use AMFI registered Phone no if you have an ARN</FieldAdditionalInfo>
                <ErrorText>{errorText.phoneNumber}</ErrorText>
              </InputErrorContainer>
            </InputNameContainer>

            <ReferralCodeContainer>
              <ReferralCheckbox
                isChecked={hasReferralCode}
                role="presentation"
                onClick={handleReferralCheckbox}
              >
                Do you have a referral code?
                <InfoIcon>
                  <img src={infoIcon} alt="info" />
                  <div className="tooltip">
                    Referral codes help us track who referred you.
                    Using a referral code has no impact on your
                    earnings or commissions.
                  </div>
                </InfoIcon>
              </ReferralCheckbox>
              {hasReferralCode && (
                <ReferralInputContainer>
                  <InputWrapper>
                    <Input
                      className="input-referral-code"
                      name="referralCode"
                      placeholder="Enter referral code"
                      type="text"
                      value={formData.referralCode || ""}
                      onChange={onChangeHandler}
                    />
                    <ValidateButton
                      onClick={handleReferralValidation}
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <Spinner color="#6725f4" size="20px" />
                      ) : (
                        "APPLY"
                      )}
                    </ValidateButton>
                  </InputWrapper>
                  {getReferralErrorMessage() && (
                    <ErrorText isSuccess={referralStatus === "valid"}>
                      {getReferralErrorMessage()}
                    </ErrorText>
                  )}
                </ReferralInputContainer>
              )}
            </ReferralCodeContainer>

            {
              errorText.captcha && errorText.captcha.length ? (
                <ErrorTextContainer>
                  <ErrorText>{errorText.captcha}</ErrorText>
                </ErrorTextContainer>
              ) : null
            }

            <ButtonContainer
              type="submit"
              onClick={submitForm}
            >
              {
                isLoading ?
                  (
                    <Spinner
                      color="#fff"
                      size="20px"
                    />
                  ) :
                  "Submit"
              }
            </ButtonContainer>

            <AgreementPolicyContainer>
              I agree to Wealthy Partner's
              Terms of services and&nbsp;
              <SafeLink
                className="privacy-policy"
                href="https://www.buildwealth.in/partners-privacy"
                rel="noopener noreferrer"
                target="_blank"
              >
                Privacy Policy
              </SafeLink>

              <CaptchaTextContainer>
                This site is protected by reCAPTCHA and the Google
                <SafeLink
                  href="https://policies.google.com/privacy"
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  &nbsp;Privacy Policy
                </SafeLink> and&nbsp;
                <SafeLink
                  href="https://policies.google.com/terms"
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  Terms of Service
                </SafeLink> apply.
              </CaptchaTextContainer>
            </AgreementPolicyContainer>

            <SignInContainer>
              <SubscribeContainer>
                <Checkbox
                  isChecked={isSubscribing}
                  role="presentation"
                  onClick={() => setIsSubscribing(!isSubscribing)}
                >
                  Subscribe to our newsletter
                </Checkbox>
              </SubscribeContainer>

              <SignIn>
                Already a partner&nbsp;
                <SafeLink
                  className="sign-in"
                  href={loginUrl}
                  rel="noopener noreferrer"
                  target="_blank"
                >Sign In</SafeLink>
              </SignIn>
            </SignInContainer>
          </Wrapper>
        ) : (
          step === 2 && (
            <OtpWrapper>
              <TextContainer>
                {isPartnerExisting ? (
                  <p className="otp-heading">Hi {formData.firstName}, Welcome Back</p>
                ) : (
                  <p className="otp-heading"><span className="step otp-heading">Step 2:</span> Verify your mobile number</p>
                )}
              </TextContainer>
              <SubText>
                {
                  isPartnerExisting ? (
                    <WelcomeText>
                      <span className="welcome-line">This number {selectedCountryCode.code} {formData.phoneNumber} is already linked to an existing account.</span>
                      Please enter the OTP sent to your number to <span className="login-text">login</span>.
                    </WelcomeText>
                  ) : (
                    `Enter the OTP sent to ${selectedCountryCode.code} ${formData.phoneNumber} to verify your number`
                  )
                }
              </SubText>
              <OtpContainer>
                <OtpInputs>
                  {
                    isLoading ?
                      (
                        <Spinner
                          color="blue"
                          size="20px"
                        />
                      ) :
                      otpContent
                  }
                </OtpInputs>
              </OtpContainer>

              <ResendOtpContainer>
                <p>Didn&apos;t receive the OTP?&nbsp;</p>
                {
                  counter === 0 ? (
                    <button
                      className="resend-btn"
                      onClick={resendOtp}
                    >
                      {
                        isResendingOtp ? (
                          <Spinner size="20px" />
                        ) :
                          "Resend"
                      }
                    </button>
                  ) : (
                    <p className="send-again-container"><span>You can resend it</span> in: {counter} sec</p>
                  )
                }
              </ResendOtpContainer>
            </OtpWrapper>
          )
        )
      }
    </>
  );
};

const CaptchaTextContainer = styled.div`
  text-align: center;
  font-size: 1.3rem;
  margin-top: 0.5rem;

  a {
    font-size: 1.3rem;
    color: #6725f4;
  }
`;

const SubscribeContainer = styled.div`
  width: 50%;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const Checkbox = styled.div`
  margin-right: 1rem;
  padding: 5px 10px 5px 20px;
  cursor: pointer;
  position: relative;
  list-style-type: none;

  &:before, &:after {
    content: "";
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    margin: auto;
    border: 1px solid #6725F4;
    border-radius: 2px;
    height: 1.6rem;
    width: 1.6rem;
  }

  ${props => props.isChecked === true &&
    css`
      &:after {
        background-image: url("https://i.wlycdn.com/wealthy-home-page/complete-stage-tick-mark.svg");
        background-size: cover;
      }
    `}

    @media (max-width: 768px) {
      text-align: left;
    }

`;

const InputErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 40%;
  text-align: left;
  :first-child {
    margin-right: 2rem;
  }

  @media (max-width: 768px) {
    width: 100%;
    margin-bottom: 2rem;

    :first-child {
      margin-right: 0;
    }
  }
`;

const ErrorText = styled.span`
  color: ${props => props.isSuccess ? "#4CAF50" : "red"};
  font-size: 1.4rem;
  margin-top: 0.5rem;
  padding-left: 0.5rem;

  @media (max-width: 768px) {
    margin-top: 0;
  }
`;

const FieldAdditionalInfo = styled(ErrorText)`
  color: #6c6c6c;
  font-size: 1.2rem;
`;

const Wrapper = styled.div`
`;

const ErrorTextContainer = styled.div`
  display: flex;
  justify-content: center;
  align-content: center;
`;

const OtpWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 50%;
  left: 50%;
  width: 100%;
  transform: translate(-50%, -50%);

  @media (max-width: 768px) {
    width: 95%;
    margin: 8rem auto 0;
    position: unset;
    transform: none;
  }
`;

const SubText = styled.div`
  font-size: 1.8rem;
  text-align: center;
  margin-top: -2rem;
  line-height: 1.43;
  width: 70%;
  &.join-us-subtext {
    margin-bottom: 4rem;
    margin-top: 2rem;
    margin-left: auto;
    margin-right: auto;
  }
  @media (max-width: 768px) {
    width: 90%;
    margin-left: auto;
    margin-right: auto;
    &.join-us-subtext {
      margin-bottom: 2rem;
    }
  }
`;

const TextContainer = styled.div`
  text-align: center;
  font-family: MavenPro;
  font-size: 2.8rem;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  color: #6c6c6c;

  .otp-heading {
    margin-bottom: 5rem;
  }

  @media(max-width: 768px) {
    width: 100%;
    font-size: 2.3rem;
    margin-top: 2rem;
  }
`;

const ResendOtpContainer = styled.div`
  display: flex;
  flex-direction: row;
  margin: 2rem auto;
  justify-content: center;
  font-family: MavenPro;
  font-size: 1.8rem;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  text-align: center;
  color: #6c6c6c;

  .resend-btn {
    border: none;
    background-color: transparent;
    cursor: pointer;
    color: #6725f4;
    font-size: 1.8rem;
    &:hover {
      transform: scale(1.02);
    }
  }

  @media (max-width: 768px) {
    flex-direction: column;
    .resend-btn, .send-again-container {
      margin-top: 1rem;
    }
  }

`;

const OtpContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 30px;
  margin-top: 2.4rem;

  @media (max-width: 768px) {
    padding: 0;
  }
`;

const OtpInputs = styled.div`
  margin: 2rem auto 1rem;
  display: flex;
  max-width: 400px;
  min-width: 250px;
  justify-content: space-around;
`;

const OtpInput = styled.input`
  text-align: center;
  font-size: 1.8rem;
  border: none;
  width: 6rem;
  height: 6rem;
  margin-right: 1rem;
  border-radius: 12px;
  background-color: #fff;
  outline: unset;
  &::placeholder {
    color: #ddd;
  }
`;

const AgreementPolicyContainer = styled.div`
  margin: 4rem auto 0;
  font-family: MavenPro;
  font-size: 1.4rem;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.71;
  letter-spacing: normal;
  text-align: center;
  color: #6c6c6c;

  .privacy-policy {
    font-weight: 500;
    color: #6725f4;
    font-size: 1.4rem;

  }

  @media (max-width: 768px) {
    width: 100%;
    line-height: 1.43;
    margin: 3rem auto 0;
  }
`;

const ButtonContainer = styled.button`
  color: #f9f9f9;
  border-radius: 1.2rem;
  background-color: #6725f4;
  font-size: 1.8rem;
  border: none;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 5rem;
  text-align: center;
  width: 25%;
  margin: 5rem auto 0;

  :hover {
    cursor: pointer;
    transition: transform 100ms linear;
    transform: scale(1.02);
  }

  ${props =>
    props.isDisabled === true &&
    css`
      background-color: #ececec;
      color: #a1a1a1;
      pointer-events: none;
      :hover {
        cursor: default;
      }
    `}

    @media (max-width: 768px) {
      width: 100%;
      margin-top: 3rem;
    }
`;

const Input = styled.input`
  background-color: #fff;
  border-radius: 1.2rem;
  border: 1px solid rgba(0, 0, 0, 0.3);
  padding: 0 2rem;
  width: 40%;
  color: #333;
  font-size: 1.8rem;
  font-weight: 400;
  display: block;
  height: 5rem;
  line-height: 1;
  box-shadow: 0 8px 5pt -5pt #D3D3D3;
  outline-width: 0px;
  -webkit-box-shadow: 0 8px 5pt -5pt #D3D3D3;
  -moz-box-shadow: 0 8px 5pt -5pt #D3D3D3;

  &.input--phone-number, &.input-email {
    width: 100%;
  }


  &.input-first-name {
    margin-right: 2rem;
  }

  -moz-appearance: textfield;
  ::-webkit-outer-spin-button,
  ::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  &::placeholder {
    color: #ccc;
    font-size: 1.8rem;
    text-align: left;
  }

  @media (max-width: 768px) {
    width: 100%;

    &.input-first-name {
      margin-right: 0;
      margin-bottom: 2rem;
    }

  }
`;

const InputNameContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 2rem;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;


const SignInContainer = styled.div`
  font-family: MavenPro;
  font-size: 1.8rem;
  font-weight: 500;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  display: flex;
  justify-content: space-between;
  color: #1c1c1c;
  margin-left: -4rem;
  margin-top: 2rem;
  width: calc(100% + 8rem);
  padding: 3rem 4rem;
  background-image: linear-gradient(to bottom, #efefef 0%, #f5f5f5 100%);

  .sign-in {
    text-decoration: underline;
    color: #6725f4;
    font-size: 1.8rem;
  }

  @media (max-width: 768px) {
    margin-left: -2rem;
    width: calc(100% + 4rem);
    text-align: center;
    flex-direction: column;
  }
`;

const SignIn = styled.div`
  width: 50%;
  text-align: right;

  @media (max-width: 768px) {
    width: 100%;
    text-align: left;
    margin-top: 0.5rem;
  }
`;

const PhoneNumberInput = styled.div`
  position: relative;
  display: flex;

  .input--phone-number {
    width: calc(100% - 5rem);
    position: relative;
    border-left: none;
    border-bottom-left-radius: unset;
    border-top-left-radius: unset;
  }

  @media (max-width: 768px) {
    width: 100%;
  }

`;

const DropDownWrapper = styled.div`
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 1.8rem;
  padding: 1rem;
  border: 1px solid rgba(0, 0, 0, 0.3);
  outline-width: 0px;
  border-top-right-radius: unset !important;
  border-bottom-right-radius: unset !important;
  border-radius: 1.2rem;
  margin: 0 auto;
  gap: 0.5rem;
`;

const SelectedItem = styled.div`
  position: relative;
  font-size: 1.5rem;
  &.active{
    color: #6725F4;
  }
`;

const IconDd = styled.img`
  height: 7px;
  transition: transform 0.1s ease-in-out;
  transform: ${({$showDropdown}) => ($showDropdown ? "rotate(180deg)" : "none")};
`;

const ReferralCodeContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 2rem;
  width: 100%;
`;

const ReferralCheckbox = styled(Checkbox)`
  font-family: MavenPro;
  font-size: 1.6rem;
  font-weight: 500;
  color: #1c1c1c;
  padding: 5px 10px 5px 25px;
  width: 40%;
  margin: 0 auto;
  display: flex;
  align-items: center;
  gap: 8px;

  &:before, &:after {
    height: 1.8rem;
    width: 1.8rem;
    border: 1px solid #6725F4;
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const InfoIcon = styled.div`
  position: relative;
  cursor: pointer;
  display: inline-flex;
  align-items: center;

  img {
    width: 15px;
    height: 15px;
  }

  .tooltip {
    visibility: hidden;
    position: absolute;
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%);
    background-color: #333;
    color: white;
    text-align: center;
    padding: 8px 12px;
    border-radius: 6px;
    font-family: MavenPro;
    font-size: 1.2rem;
    font-weight: normal;
    width: 200px;
    z-index: 1;
    margin-bottom: 8px;
  }

  &:hover .tooltip {
    visibility: visible;
  }

  .tooltip::after {
    content: "";
    position: absolute;
    top: 100%;
    left: 50%;
    margin-left: -5px;
    border-width: 5px;
    border-style: solid;
    border-color: #333 transparent transparent transparent;
  }
`;

const ReferralInputContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 40%;
  margin-top: 1rem;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const InputWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;

  .input-referral-code {
    width: 100%;
  }
`;

const ValidateButton = styled.button`
  background: none;
  border: none;
  color: #6725f4;
  font-family: MavenPro;
  font-size: 1.6rem;
  font-weight: 400;
  padding: 0;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    opacity: 0.8;
  }

  &:disabled {
    color: #a1a1a1;
    cursor: not-allowed;
  }
`;

const WelcomeText = styled.div`
  text-align: center;
  line-height: 1.6;
  font-size: 1.8rem;
  color: #6c6c6c;
  font-family: Lato;
  font-weight: normal;

  .welcome-line {
    font-family: Lato;
    font-weight: 600;
    display: block;
    margin-bottom: 0.3rem;
  }

  .login-text {
    font-family: Lato;
    font-weight: 600;
  }
`;

export default Form;
